import React, { useState, useMemo, useEffect } from 'react';
import { format, parseISO, differenceInDays, startOfYear, endOfYear } from 'date-fns';
import {
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  FileText,
  Shield,
  User,
  ChevronLeft,
  ChevronRight,
  Eye,
  AlertCircle,
  RotateCw
} from 'lucide-react';
import { useComplianceTaskDetails } from '../../hooks/useComplianceTaskDetails';

const ComplianceTimeline = ({ className = "", events = [], isLoading = false }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const complianceDetails = useComplianceTaskDetails();

  const selectedComplianceDetails = useMemo(() => {
    if (!selectedEvent || selectedEvent.type !== 'compliance') {
      return null;
    }

    const possibleKey = selectedEvent.detailsKey
      || selectedEvent.complianceKey
      || (typeof selectedEvent.id === 'string'
        ? selectedEvent.id.replace(/-\d{4}$/i, '')
        : null);

    if (!possibleKey) {
      return null;
    }

    return complianceDetails[possibleKey] || null;
  }, [selectedEvent, complianceDetails]);

  const renderRichText = (text) => {
    if (!text) {
      return null;
    }

    const urlRegex = /(https?:\/\/[^\s)]+)/g;

    return text.split(urlRegex).map((part, index) => {
      if (part.startsWith('http://') || part.startsWith('https://')) {
        const sanitizedUrl = part.replace(/[),.]+$/, '');
        const trailing = part.slice(sanitizedUrl.length);

        return (
          <React.Fragment key={`link-${index}`}>
            <a
              href={sanitizedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-700 break-words"
            >
              {sanitizedUrl}
            </a>
            {trailing}
          </React.Fragment>
        );
      }

      return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
    });
  };

  const timelineEvents = useMemo(() => {
    if (!Array.isArray(events)) {
      return [];
    }

    const now = new Date();

    return events
      .map((event, index) => {
        if (!event) {
          return null;
        }

        const rawDate = event.date;
        let parsedDate = null;

        if (rawDate instanceof Date) {
          parsedDate = rawDate;
        } else if (typeof rawDate === 'string') {
          try {
            parsedDate = parseISO(rawDate);
          } catch (error) {
            parsedDate = null;
          }
        }

        if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
          return null;
        }

        const isoDate = parsedDate.toISOString();
        const daysRemaining = typeof event.daysRemaining === 'number'
          ? event.daysRemaining
          : differenceInDays(parsedDate, now);

        const derivedStatus = event.status
          || (event.type === 'violation'
            ? 'violation'
            : event.type === 'deadline'
              ? (daysRemaining < 0 ? 'violation' : 'upcoming')
              : 'completed');

        const derivedPriority = event.priority
          || (derivedStatus === 'violation'
            ? 'critical'
            : event.type === 'deadline'
              ? (daysRemaining <= 7 ? 'critical' : 'high')
              : 'medium');

        return {
          id: event.id || `timeline-event-${index}`,
          ...event,
          date: isoDate,
          status: derivedStatus,
          priority: derivedPriority,
          entityName: event.entityName || 'Fleet Operations',
          entityType: event.entityType || 'company',
          daysRemaining
        };
      })
      .filter(Boolean)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [events]);

  const availableYears = useMemo(() => {
    const years = Array.from(new Set(
      timelineEvents.map(event => {
        const date = new Date(event.date);
        return Number.isNaN(date.getTime()) ? null : date.getFullYear();
      }).filter((value) => value !== null)
    )).sort((a, b) => b - a);

    if (years.length === 0) {
      return [new Date().getFullYear()];
    }

    return years;
  }, [timelineEvents]);

  useEffect(() => {
    if (!availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);


  // Filter events by selected year and filter type
  const filteredEvents = useMemo(() => {
    const yearStart = startOfYear(new Date(selectedYear, 0, 1));
    const yearEnd = endOfYear(new Date(selectedYear, 0, 1));

    return timelineEvents
      .filter(event => {
        let eventDate;
        try {
          eventDate = parseISO(event.date);
        } catch (parseError) {
          return false;
        }

        if (!eventDate || Number.isNaN(eventDate.getTime())) {
          return false;
        }

        const isInYear = eventDate >= yearStart && eventDate <= yearEnd;

        if (!isInYear) return false;

        if (selectedFilter === 'all') return true;
        return event.type === selectedFilter;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [selectedYear, selectedFilter, timelineEvents]);

  // Group events by month
  const eventsByMonth = useMemo(() => {
    const monthMap = new Map();

    filteredEvents.forEach(event => {
      const eventDate = parseISO(event.date);
      if (Number.isNaN(eventDate.getTime())) {
        return;
      }

      const key = format(eventDate, 'yyyy-MM');
      const label = format(eventDate, 'MMMM yyyy');

      if (!monthMap.has(key)) {
        monthMap.set(key, { label, events: [] });
      }

      monthMap.get(key).events.push(event);
    });

    const ordered = Array.from(monthMap.entries()).sort((a, b) => a[0].localeCompare(b[0]));

    ordered.forEach(([, value]) => {
      value.events.sort((a, b) => new Date(a.date) - new Date(b.date));
    });

    return ordered;
  }, [filteredEvents]);

  const getEventIcon = (type, category) => {
    // Special handling for IFTA events
    if (category === 'ifta') {
      return <FileText className="h-5 w-5" />;
    }
    
    switch (type) {
      case 'renewal':
        return <RefreshCw className="h-5 w-5" />;
      case 'violation':
        return <AlertTriangle className="h-5 w-5" />;
      case 'inspection':
        return <Shield className="h-5 w-5" />;
      case 'compliance':
        return <FileText className="h-5 w-5" />;
      case 'deadline':
        return <Clock className="h-5 w-5" />;
      case 'training':
        return <User className="h-5 w-5" />;
      case 'certification':
        return <FileText className="h-5 w-5" />;
      case 'audit':
        return <Eye className="h-5 w-5" />;
      default:
        return <Calendar className="h-5 w-5" />;
    }
  };

  const getEventColor = (type, status, priority) => {
    if (status === 'violation' || priority === 'critical') {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    if (status === 'warning' || priority === 'high') {
      return 'bg-orange-100 text-orange-800 border-orange-200';
    }
    if (status === 'upcoming') {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    }
    if (status === 'completed' || status === 'passed' || status === 'resolved') {
      return 'bg-green-100 text-green-800 border-green-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status, type) => {
    switch (status) {
      case 'completed':
      case 'passed':
      case 'active':
        return <CheckCircle className="h-4 w-4" />;
      case 'resolved':
        return <RotateCw className="h-4 w-4" />;
      case 'warning':
      case 'upcoming':
        return <AlertCircle className="h-4 w-4" />;
      case 'violation':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Calendar className="h-4 w-4" />;
    }
  };

  const filterOptions = [
    { value: 'all', label: 'All Events', count: timelineEvents.length },
    { value: 'renewal', label: 'Renewals', count: timelineEvents.filter(e => e.type === 'renewal').length },
    { value: 'violation', label: 'Violations', count: timelineEvents.filter(e => e.type === 'violation').length },
    { value: 'inspection', label: 'Inspections', count: timelineEvents.filter(e => e.type === 'inspection').length },
    { value: 'deadline', label: 'Deadlines', count: timelineEvents.filter(e => e.type === 'deadline').length },
    { value: 'training', label: 'Training', count: timelineEvents.filter(e => e.type === 'training').length },
    { value: 'certification', label: 'Certifications', count: timelineEvents.filter(e => e.type === 'certification').length },
    { value: 'audit', label: 'Audits', count: timelineEvents.filter(e => e.type === 'audit').length },
    { value: 'compliance', label: 'Compliance Tasks', count: timelineEvents.filter(e => e.type === 'compliance').length }
  ];

  return (
    <div className={`bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Compliance Timeline</h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Historical events and upcoming deadlines</p>
          </div>
          
          {/* Year Navigation */}
          <div className="flex items-center justify-center sm:justify-end space-x-4 mt-3 sm:mt-0">
            <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-1">
              <button
                onClick={() => setSelectedYear(selectedYear - 1)}
                className="p-1.5 sm:p-2 hover:bg-white hover:shadow-sm rounded-md transition-all"
              >
                <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
              </button>
              <span className="text-base sm:text-lg font-semibold text-gray-900 min-w-[60px] sm:min-w-[80px] text-center px-2">
                {selectedYear}
              </span>
              <button
                onClick={() => setSelectedYear(selectedYear + 1)}
                className="p-1.5 sm:p-2 hover:bg-white hover:shadow-sm rounded-md transition-all"
              >
                <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="mt-4 sm:mt-6">
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {filterOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setSelectedFilter(option.value)}
                className={`inline-flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-md sm:rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                  selectedFilter === option.value
                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                <span className="truncate">{option.label}</span>
                <span className="ml-1 sm:ml-2 bg-white bg-opacity-80 text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full flex-shrink-0">
                  {option.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline Content */}
      <div className="p-4 sm:p-6">
        {eventsByMonth.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            {isLoading ? (
              <>
                <RefreshCw className="h-8 w-8 sm:h-12 sm:w-12 text-blue-500 mx-auto mb-3 sm:mb-4 animate-spin" />
                <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Loading timeline</h4>
                <p className="text-sm sm:text-base text-gray-600">Syncing compliance history and upcoming deadlines.</p>
              </>
            ) : (
              <>
                <Calendar className="h-8 w-8 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No events found</h4>
                <p className="text-sm sm:text-base text-gray-600">No compliance events found for {selectedYear} with the selected filters.</p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-6 sm:space-y-8">
            {eventsByMonth.map(([monthKey, { label, events }]) => (
              <div key={monthKey}>
                <div className="flex items-center mb-3 sm:mb-4">
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900">{label}</h4>
                  <div className="flex-1 h-px bg-gray-200 ml-3 sm:ml-4"></div>
                </div>
                
                <div className="space-y-3 sm:space-y-4">
                  {events.map((event) => (
                    <div
                      key={event.id}
                      className="flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer active:bg-gray-100"
                      onClick={() => setSelectedEvent(event)}
                    >
                      <div className={`flex-shrink-0 p-1.5 sm:p-2 rounded-md sm:rounded-lg border ${getEventColor(event.type, event.status, event.priority)}`}>
                        {React.cloneElement(getEventIcon(event.type, event.category), {
                          className: "h-3 w-3 sm:h-5 sm:w-5"
                        })}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                          <h5 className="text-sm sm:text-base font-medium text-gray-900 truncate">{event.title}</h5>
                          <div className="flex items-center space-x-2 mt-1 sm:mt-0 flex-shrink-0">
                            <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium border ${getEventColor(event.type, event.status, event.priority)}`}>
                              {React.cloneElement(getStatusIcon(event.status, event.type), {
                                className: "h-2.5 w-2.5 sm:h-4 sm:w-4"
                              })}
                              <span className="ml-1 capitalize hidden sm:inline">{event.status.replace('_', ' ')}</span>
                            </span>
                            <span className="text-xs text-gray-500">
                              {format(parseISO(event.date), 'MMM dd')}
                            </span>
                          </div>
                        </div>
                        
                        <p className="text-xs sm:text-sm text-gray-600 mb-2 line-clamp-2">{event.description}</p>
                        
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span className="truncate">{event.entityName}</span>
                          {event.documentNumber && (
                            <span className="font-mono text-xs hidden sm:inline flex-shrink-0 ml-2">{event.documentNumber}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedEvent(null)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-t-lg sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center flex-1 min-w-0">
                    <div className={`flex-shrink-0 p-2 sm:p-3 rounded-lg border ${getEventColor(selectedEvent.type, selectedEvent.status, selectedEvent.priority)}`}>
                      {React.cloneElement(getEventIcon(selectedEvent.type, selectedEvent.category), {
                        className: "h-4 w-4 sm:h-5 sm:w-5"
                      })}
                    </div>
                    <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{selectedEvent.title}</h3>
                      <p className="text-xs sm:text-sm text-gray-600">{format(parseISO(selectedEvent.date), 'MMMM dd, yyyy')}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedEvent(null)}
                    className="text-gray-400 hover:text-gray-600 p-1 ml-2 flex-shrink-0"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  {selectedComplianceDetails && (
                    <div className="space-y-6">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">What is this task?</h4>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {renderRichText(selectedComplianceDetails.overview)}
                        </p>
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Why it matters</h4>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {renderRichText(selectedComplianceDetails.importance)}
                        </p>
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">How & where to file</h4>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {renderRichText(selectedComplianceDetails.filing)}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-blue-50 border border-blue-100 rounded-lg p-4">
                        <div>
                          <h5 className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-1">Due Date</h5>
                          <p className="text-sm text-blue-900">
                            {format(parseISO(selectedEvent.date), 'MMMM dd, yyyy')}
                          </p>
                        </div>
                        <div>
                          <h5 className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-1">Priority</h5>
                          <p className="text-sm capitalize text-blue-900">{selectedEvent.priority || 'standard'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <h4 className="text-xs sm:text-sm font-medium text-gray-900 mb-2">Description</h4>
                    <p className="text-xs sm:text-sm text-gray-600">{selectedEvent.description}</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Entity</h4>
                      <p className="text-sm text-gray-600">{selectedEvent.entityName}</p>
                      <p className="text-xs text-gray-500 capitalize">{selectedEvent.entityType}</p>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Status</h4>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getEventColor(selectedEvent.type, selectedEvent.status, selectedEvent.priority)}`}>
                        {getStatusIcon(selectedEvent.status, selectedEvent.type)}
                        <span className="ml-1 capitalize">{selectedEvent.status.replace('_', ' ')}</span>
                      </span>
                    </div>

                    {selectedEvent.documentNumber && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Document Number</h4>
                        <p className="text-sm text-gray-600 font-mono">{selectedEvent.documentNumber}</p>
                      </div>
                    )}

                    {selectedEvent.issuingAuthority && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Issuing Authority</h4>
                        <p className="text-sm text-gray-600">{selectedEvent.issuingAuthority}</p>
                      </div>
                    )}

                    {selectedEvent.cost && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Cost</h4>
                        <p className="text-sm text-gray-600">${selectedEvent.cost.toLocaleString()}</p>
                      </div>
                    )}

                    {selectedEvent.fine && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Fine Amount</h4>
                        <p className="text-sm text-red-600">${selectedEvent.fine.toLocaleString()}</p>
                      </div>
                    )}

                    {selectedEvent.nextRenewal && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Next Renewal</h4>
                        <p className="text-sm text-gray-600">{format(parseISO(selectedEvent.nextRenewal), 'MMMM dd, yyyy')}</p>
                      </div>
                    )}

                    {selectedEvent.daysRemaining !== undefined && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Days Remaining</h4>
                        <p className={`text-sm ${selectedEvent.daysRemaining < 30 ? 'text-red-600' : 'text-gray-600'}`}>
                          {selectedEvent.daysRemaining} days
                        </p>
                      </div>
                    )}

                    {selectedEvent.estimatedTax && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Estimated Tax</h4>
                        <p className="text-sm text-red-600">${selectedEvent.estimatedTax.toLocaleString()}</p>
                      </div>
                    )}

                    {selectedEvent.estimatedCost && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Estimated Cost</h4>
                        <p className="text-sm text-orange-600">${selectedEvent.estimatedCost.toLocaleString()}</p>
                      </div>
                    )}

                    {selectedEvent.nextFiling && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Next Filing</h4>
                        <p className="text-sm text-gray-600">{format(parseISO(selectedEvent.nextFiling), 'MMMM dd, yyyy')}</p>
                      </div>
                    )}
                  </div>

                  {selectedEvent.resolutionAction && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Resolution Action</h4>
                      <p className="text-sm text-gray-600">{selectedEvent.resolutionAction}</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceTimeline;
